import React, { useState } from 'react'
import { LuEye } from '@react-icons/all-files/lu/LuEye'
import { LuEyeOff } from '@react-icons/all-files/lu/LuEyeOff'
import type { PasswordStrength } from 'types/business'
import PasswordStrengthIndicator from 'components/common/password-strength-indicator'
import { AccessibleIcon, Box, IconButton, TextField } from 'components/ds'
type Props = React.ComponentProps<typeof TextField.Root> & {
  strength?: PasswordStrength
  initialReveal?: boolean
  value?: string
}

/**
 * Password input field with show/hide toggle functionality and optional password strength indicator.
 *
 * Extends Radix UI TextField with visibility controls and strength validation display.
 * Includes an eye/eye-off icon button for toggling password visibility.
 *
 * @param strength - Optional password strength object for validation display
 * @param initialReveal - Whether to initially show the password (default: false)
 * @param rest - All other props passed to the underlying TextField component
 * @see https://www.radix-ui.com/themes/docs/components/text-field - Radix UI TextField documentation
 */
const PasswordTextField = ({
  strength,
  initialReveal,
  ...rest
}: Props): React.ReactNode => {
  const { value, size } = rest
  const [reveal, setReveal] = useState(Boolean(initialReveal))
  const Icon = reveal ? LuEye : LuEyeOff
  return (
    <Box>
      <TextField.Root {...rest} type={reveal ? 'text' : 'password'}>
        <TextField.Slot side="right">
          <IconButton
            size={size}
            color="gray"
            variant="ghost"
            type="button"
            onClick={() => setReveal(!reveal)}
          >
            <AccessibleIcon label="show password">
              <Icon />
            </AccessibleIcon>
          </IconButton>
        </TextField.Slot>
      </TextField.Root>
      {strength && (
        <PasswordStrengthIndicator
          password={value ?? ''}
          passwordStrength={strength}
        />
      )}
    </Box>
  )
}

export default PasswordTextField
